import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DragDropModule} from 'primeng/dragdrop';
import {Skeleton} from 'primeng/skeleton';
import {ButtonIconComponent} from '../../../../../shared/components/button-icon/button-icon.component';
import {DialogBase} from '../../../../../shared/components/dialogs/dialog-base/dialog-base';
import {InputItemSelection} from '../../../../../shared/components/input-item-selection/input-item-selection';
import {TranslateSafePipe} from '../../../../../shared/pipes/translation.pipe';
import {
  AlarmRuleConditionDto,
  AlarmRuleConditionType,
  ConditionOperator,
} from '../../../../../types/dtos/alarm-rule-condition.dto';
import {InputItemSelectInterface} from '../../../../../types/interfaces/input.interface';
import {BaseRuleCondition} from './rule-conditions/base-rule-condition';

@Component({
  selector: 'app-alarm-rule-conditions',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    ButtonIconComponent,
    DialogBase,
    InputItemSelection,
    CdkDragHandle,
    CdkDropList,
    CdkDrag,
    Skeleton,
    TranslateSafePipe,
  ],
  templateUrl: './alarm-rule-conditions.html',
  styleUrl: './alarm-rule-conditions.css',
})
export class AlarmRuleConditions {
  @Input()
  mode: 'create' | 'override' | 'edit' | 'view' = 'view';

  @Input()
  public formGroup!: FormGroup;

  @Input()
  public isLoading: boolean = false;

  @Input()
  public conditions: AlarmRuleConditionDto[] = [];

  public addDialogOpen: boolean = false;
  public createGroup: FormGroup = new FormGroup({
    type: new FormControl(),
  });
  public TypeSelect: InputItemSelectInterface = {
    formGroup: this.createGroup,
    isTranslated: true,
    label: 'common.input.select-alarm-rule-condition.label',
    required: true,
    id: 'type',
    options: [],
  };

  private _conditionDefinitions!: Record<AlarmRuleConditionType, BaseRuleCondition>;

  @Input({required: true})
  public set conditionDefinitions(value: Record<AlarmRuleConditionType, BaseRuleCondition>) {
    this._conditionDefinitions = value;
    this.TypeSelect.options = Object.values(AlarmRuleConditionType)
      .map((type) => {
        let definition: BaseRuleCondition = this._conditionDefinitions[type];

        return {
          label: definition?.getName() ?? 'Unknown',
          value: type,
          description: definition?.getDescription() ?? '',
          icon: definition?.getIcon() ?? 'question_mark',
        };
      });
  }

  public get conditionsForm(): FormArray<FormGroup> {
    return this.formGroup.get('conditions') as FormArray<FormGroup>;
  }

  public dropCondition(event: CdkDragDrop<FormGroup[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.moveItemInFormArray(this.conditionsForm, event.previousIndex, event.currentIndex);
    this.updateSortIndexes();
  }

  public getIndentLevel(index: number): number {
    return this.getIndentLevels()[index] ?? 0;
  }

  public getConditionGridRow(index: number): string {
    const startRow: number = index * 2 + 1;
    return `${startRow} / span 2`;
  }

  public getLogicGridRow(index: number): string {
    const startRow: number = index * 2;
    return `${startRow} / span 2`;
  }

  public getEndSpacerRow(): string {
    return `${this.conditions.length * 2} / span 1`;
  }

  public getOperator(index: number): ConditionOperator {
    return this.conditionsForm.at(index)
      .get('operator')?.value ?? 'AND';
  }

  public toggleOperator(index: number): void {
    const control = this.conditionsForm.at(index)
      .get('operator') as FormControl<ConditionOperator | null>;
    const current: ConditionOperator = control.value ?? 'AND';

    control.setValue(current === 'AND' ? 'OR' : 'AND');
  }

  public createConditionOnClick(): void {
    this.addDialogOpen = true;
  }

  public onTypeSelect(type: string): void {
    this.addDialogOpen = false;

    this.addCondition(type as AlarmRuleConditionType);
  }

  public addCondition(type: AlarmRuleConditionType): void {
    if (this.mode !== 'create' && this.mode !== 'edit') return;

    this.conditionsForm.push(this._conditionDefinitions[type].createFormGroup({
      type,
      operator: 'AND',
      sort_index: this.conditionsForm.length,
      config: {},
    }));
  }

  public removeCondition(index: number): void {
    if (this.mode !== 'create' && this.mode !== 'edit') return;

    this.conditionsForm.removeAt(index);
  }

  public getConditionType(index: number): AlarmRuleConditionType | null {
    return this.conditionsForm.at(index)
      .get('type')?.value;
  }

  public getDefinition(type: AlarmRuleConditionType): any {
    return this._conditionDefinitions[type];
  }

  public getName(index: number): string {
    let type: AlarmRuleConditionType | null = this.getConditionType(index);
    if (!type) return '';
    return this._conditionDefinitions[type]?.getName() ?? 'Unknown';
  }

  public getIcon(index: number): string {
    let type: AlarmRuleConditionType | null = this.getConditionType(index);
    if (!type) return '';
    return this._conditionDefinitions[type]?.getIcon() ?? 'question_mark';
  }

  public getCondition(index: number): AlarmRuleConditionDto | undefined {
    let id = this.conditionsForm.at(index)
      .get('id')?.value;
    return this.conditions.find((a) => a.id === id);
  }

  private moveItemInFormArray(
    formArray: FormArray,
    fromIndex: number,
    toIndex: number,
  ): void {
    const from: number = this.clamp(fromIndex, formArray.length - 1);
    const to: number = this.clamp(toIndex, formArray.length - 1);

    if (from === to) {
      return;
    }

    const target = formArray.at(from);
    const delta = to < from ? -1 : 1;

    for (let i = from; i !== to; i += delta) {
      const current = formArray.at(i + delta);
      formArray.setControl(i, current);
    }

    formArray.setControl(to, target);
  }

  private clamp(value: number, max: number): number {
    return Math.max(0, Math.min(max, value));
  }

  private updateSortIndexes(): void {
    this.conditionsForm.controls.forEach((control, index) => {
      control.get('sortIndex')
        ?.setValue(index, {emitEvent: false});
    });
  }

  private getIndentLevels(): number[] {
    const levels: number[] = [];

    let currentLevel: number = 0;
    const blockStack: number[] = [0];

    for (let i = 0; i < this.conditionsForm.length; i++) {
      if (i === 0) {
        levels.push(0);
        continue;
      }

      const operator: ConditionOperator = this.getOperator(i);

      if (operator === 'AND') {
        currentLevel = blockStack[blockStack.length - 1] + 1;
        blockStack.push(currentLevel);
        levels.push(currentLevel);
        continue;
      }

      if (operator === 'OR') {
        if (blockStack.length > 1) {
          blockStack.pop();
        }

        currentLevel = blockStack[blockStack.length - 1];
        levels.push(currentLevel);
        continue;
      }

      levels.push(currentLevel);
    }

    return levels;
  }
}
